import type { HoverSnapshot } from "./types";

function safeId(el: Element) {
  const id = (el as HTMLElement).id;
  return id ? id : null;
}

function safeClassName(el: Element) {
  const cn = (el as HTMLElement).className;
  if (!cn) return null;
  if (typeof cn === "string") return cn;
  return null;
}

function summarizeElement(el: Element) {
  return {
    tag: el.tagName.toLowerCase(),
    id: safeId(el),
    className: safeClassName(el),
    role: el.getAttribute("role"),
  };
}

function summarizeComputed(el: Element) {
  const cs = window.getComputedStyle(el as HTMLElement);
  return {
    backgroundColor: cs.backgroundColor,
    color: cs.color,
    boxShadow: cs.boxShadow,
    opacity: cs.opacity,
    transform: cs.transform,
    filter: cs.filter,
    pointerEvents: cs.pointerEvents,
    cursor: cs.cursor,
  };
}

function findInteractiveTarget(el: Element | null): Element | null {
  if (!el) return null;
  return (
    el.closest?.("button, a, [role=button], input[type=button], input[type=submit]") ??
    null
  );
}

export function takeHoverSnapshot(params: {
  x: number;
  y: number;
  media: HoverSnapshot["matchMedia"];
}): HoverSnapshot {
  const { x, y, media } = params;

  const hovered = Array.from(document.querySelectorAll(":hover"));
  const hoverChain = hovered.slice(-8).map((el) => ({
    tag: el.tagName.toLowerCase(),
    id: safeId(el),
    className: safeClassName(el),
  }));

  const el = document.elementFromPoint(x, y);
  const interactive = findInteractiveTarget(el);

  const elementsFromPoint = (document.elementsFromPoint?.(x, y) ?? [])
    .slice(0, 6)
    .map(summarizeElement);

  const elementFromPoint = el ? summarizeElement(el) : null;
  const elementFromPointIsHovered = !!el && (el as HTMLElement).matches(":hover");

  const interactiveTarget = interactive ? summarizeElement(interactive) : null;
  const interactiveTargetIsHovered = !!interactive && (interactive as HTMLElement).matches(":hover");

  const computed = {
    elementFromPoint: el ? summarizeComputed(el) : null,
    interactiveTarget: interactive ? summarizeComputed(interactive) : null,
  };

  const pointerOverOverlay = !!el && !!el.closest?.('[data-hover-diagnostics-root="1"]');

  const openNativeDialogs = document.querySelectorAll("dialog[open]").length;
  const radixOpenOverlays = document.querySelectorAll(
    '[data-state="open"][data-radix-dialog-overlay], [data-state="open"][data-radix-popper-content], [data-state="open"][data-radix-portal]'
  ).length;
  const bodyScrollLocked = document.body.hasAttribute("data-scroll-locked");

  const active = document.activeElement;
  const activeElement = active
    ? `${active.tagName.toLowerCase()}${(active as HTMLElement).id ? `#${(active as HTMLElement).id}` : ""}`
    : "(none)";

  return {
    ts: new Date().toISOString(),
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio || 1,
    document: {
      visibilityState: document.visibilityState,
      hasFocus: document.hasFocus(),
    },
    matchMedia: media,
    pointer: { x, y },
    elementFromPoint,
    elementFromPointIsHovered,
    elementsFromPoint,
    interactiveTarget,
    interactiveTargetIsHovered,
    hoverChain,
    computed,
    diagnostics: {
      pointerOverOverlay,
    },
    dialogs: {
      openNativeDialogs,
      radixOpenOverlays,
      bodyScrollLocked,
    },
    focus: { activeElement },
  };
}
